import { useQuery } from '@tanstack/react-query';
import { BarChart3 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import type { ShellContext } from '@/components/layout/AppShell';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { statsApi, teamsApi } from '@/lib/api/endpoints';
import type { Stats } from '@/lib/api/types';
import { useT } from '@/lib/i18n';
import { formatMinutes } from '@/routes/Clients';
import { Page } from '@/routes/Today';
import { cn } from '@/lib/utils';

type Range = 30 | 90 | 365;
type Scope = { kind: 'me' } | { kind: 'team'; id: string; name: string };

/**
 * How much, how long, who — never what.
 *
 * Every number on this screen is a count, a duration or a sum, and all of
 * them come from one window chosen once at the top, so the KPI row, the trend
 * and every breakdown are the same slice and cannot disagree. Team numbers
 * are the team manager's business: the scope row offers only the teams the
 * viewer runs, and the server refuses anybody else.
 *
 * Charts are inline SVG — five of them on one page do not justify a library,
 * and the palette below was validated for colour-vision deficiency against
 * both of MirOnDo's surfaces rather than eyeballed.
 */
export function StatsRoute() {
  const t = useT();
  const { currentUser } = useOutletContext<ShellContext>();
  const [scope, setScope] = useState<Scope>({ kind: 'me' });
  const [days, setDays] = useState<Range>(90);

  const { data: teams } = useQuery({ queryKey: ['teams'], queryFn: () => teamsApi.list() });
  const manageable = (teams ?? []).filter((team) => team.can_manage);

  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: ['stats', scope, days],
    queryFn: () =>
      statsApi.get(scope.kind, days, scope.kind === 'team' ? scope.id : null),
    placeholderData: (previous) => previous,
  });

  return (
    <Page>
      <header className="mb-5">
        <h1 className="text-xl font-semibold tracking-tight">{t('stats.title')}</h1>
        <p className="mt-1 text-sm text-content-muted">{t('stats.subtitle')}</p>
      </header>

      {/* One row, above everything it scopes: range first, then who. */}
      <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-2">
        <Segmented
          value={String(days)}
          onChange={(value) => setDays(Number(value) as Range)}
          options={[
            { value: '30', label: t('stats.range.30') },
            { value: '90', label: t('stats.range.90') },
            { value: '365', label: t('stats.range.365') },
          ]}
        />
        {manageable.length > 0 || currentUser.is_superadmin ? (
          <Segmented
            value={scope.kind === 'me' ? 'me' : scope.id}
            onChange={(value) => {
              const team = manageable.find((row) => row.id === value);
              setScope(team ? { kind: 'team', id: team.id, name: team.name } : { kind: 'me' });
            }}
            options={[
              { value: 'me', label: t('stats.scope.me') },
              ...manageable.map((team) => ({ value: team.id, label: team.name })),
            ]}
          />
        ) : null}
      </div>

      {isPending ? (
        <LoadingState />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : data ? (
        // Refetch keeps the frame: the previous render stays, dimmed.
        <div className={cn('viz-root space-y-6', isFetching && 'opacity-60 transition-opacity')}>
          <Dashboard data={data} />
        </div>
      ) : null}

      <style>{VIZ_TOKENS}</style>
    </Page>
  );
}

function Dashboard({ data }: { data: Stats }) {
  const t = useT();
  const nothing =
    data.totals.completed_in_range === 0 &&
    data.totals.open === 0 &&
    data.totals.tracked_minutes === 0;

  if (nothing) {
    return (
      <EmptyState
        icon={<BarChart3 className="h-5 w-5" />}
        title={t('stats.empty')}
        hint={t('stats.subtitle')}
      />
    );
  }

  const multi = data.by_user.length > 1;

  return (
    <>
      <KpiRow data={data} />

      <Card title={t('stats.trend.title')}>
        <TrendChart points={data.completed_per_week} />
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title={t('stats.people.title')}>
          <PeopleBars rows={data.by_user} showLegend={true} />
        </Card>
        <Card title={t('stats.hours.title')}>
          <HoursBars rows={data.by_user} />
        </Card>
      </div>

      <Card title={t('stats.projects.title')}>
        <ProjectsTable rows={data.by_project} />
      </Card>

      {multi ? (
        <Card title={t('stats.matrix.title')}>
          <Matrix data={data} />
        </Card>
      ) : null}

      <HandoverTile data={data.handovers} />
    </>
  );
}

// ---------------------------------------------------------------------------
// KPI row — the numbers are the chart
// ---------------------------------------------------------------------------

function KpiRow({ data }: { data: Stats }) {
  const t = useT();
  const { totals } = data;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
      <Tile label={t('stats.kpi.completed')} value={String(totals.completed_in_range)} />
      <Tile label={t('stats.kpi.created')} value={String(totals.created_in_range)} />
      <Tile label={t('stats.kpi.open')} value={String(totals.open)} />
      <Tile
        label={t('stats.kpi.overdue')}
        value={String(totals.overdue)}
        tone={totals.overdue > 0 ? 'warning' : 'plain'}
      />
      <Tile label={t('stats.kpi.tracked')} value={hours(totals.tracked_minutes)} />
      <Tile
        label={t('stats.kpi.median')}
        value={totals.median_days_to_complete === null ? '—' : t('stats.days', { value: totals.median_days_to_complete })}
        note={t('stats.kpi.medianNote')}
      />
    </div>
  );
}

function Tile({
  label,
  value,
  note,
  tone = 'plain',
}: {
  label: string;
  value: string;
  note?: string;
  tone?: 'plain' | 'warning';
}) {
  return (
    <div className="rounded-xl border border-border bg-surface px-4 py-3">
      <p className="text-[11px] uppercase tracking-wide text-content-subtle">{label}</p>
      {/* Proportional figures on a standalone number; tabular only in columns. */}
      <p className={cn('mt-1 text-2xl font-semibold', tone === 'warning' && 'text-amber')}>
        {value}
      </p>
      {note ? <p className="text-[11px] text-content-subtle">{note}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trend — one series, line + wash, crosshair tooltip
// ---------------------------------------------------------------------------

function TrendChart({ points }: { points: Stats['completed_per_week'] }) {
  const t = useT();
  const [hover, setHover] = useState<number | null>(null);
  const width = 640;
  const height = 180;
  const pad = { left: 32, right: 16, top: 12, bottom: 28 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const max = Math.max(1, ...points.map((p) => p.count));
  const step = points.length > 1 ? innerW / (points.length - 1) : innerW;
  const x = (i: number) => pad.left + i * step;
  const y = (v: number) => pad.top + innerH - (v / max) * innerH;
  const ticks = niceTicks(max);

  const last = points.length - 1;
  const end = points[last];
  const at = hover === null ? undefined : points[hover];
  if (end === undefined) return null;

  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.count)}`).join(' ');
  const area = `${path} L${x(last)},${y(0)} L${x(0)},${y(0)} Z`;

  return (
    <figure className="m-0">
      <div className="relative">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="h-auto w-full"
          role="img"
          aria-label={t('stats.trend.title')}
          onPointerMove={(event) => {
            const box = event.currentTarget.getBoundingClientRect();
            const px = ((event.clientX - box.left) / box.width) * width;
            const index = Math.round((px - pad.left) / step);
            setHover(Math.max(0, Math.min(last, index)));
          }}
          onPointerLeave={() => setHover(null)}
        >
          {ticks.map((tick) => (
            <g key={tick}>
              <line x1={pad.left} x2={width - pad.right} y1={y(tick)} y2={y(tick)} className="viz-grid" />
              <text x={pad.left - 6} y={y(tick) + 3} textAnchor="end" className="viz-tick">
                {tick}
              </text>
            </g>
          ))}
          <path d={area} className="viz-area" />
          <path d={path} className="viz-line" fill="none" />
          {/* Endpoint only: the one direct label the eye needs. */}
          <circle cx={x(last)} cy={y(end.count)} r={4} className="viz-dot" />
          <text x={x(last)} y={y(end.count) - 8} textAnchor="middle" className="viz-value">
            {end.count}
          </text>
          {hover !== null && at ? (
            <>
              <line x1={x(hover)} x2={x(hover)} y1={pad.top} y2={pad.top + innerH} className="viz-crosshair" />
              <circle cx={x(hover)} cy={y(at.count)} r={4} className="viz-dot" />
            </>
          ) : null}
          {points.map((p, i) =>
            i % Math.ceil(points.length / 8) === 0 ? (
              <text key={p.week_start} x={x(i)} y={height - 8} textAnchor="middle" className="viz-tick">
                {shortDay(p.week_start)}
              </text>
            ) : null,
          )}
        </svg>
        {hover !== null && at ? (
          <Tooltip
            left={(x(hover) / width) * 100}
            rows={[{ label: shortDay(at.week_start), value: String(at.count) }]}
          />
        ) : null}
      </div>
    </figure>
  );
}

// ---------------------------------------------------------------------------
// People — two series (open / completed), stacked, legend + tooltip
// ---------------------------------------------------------------------------

function PeopleBars({ rows, showLegend }: { rows: Stats['by_user']; showLegend: boolean }) {
  const t = useT();
  const [hover, setHover] = useState<string | null>(null);
  const max = Math.max(1, ...rows.map((r) => r.open + r.completed));

  return (
    <div>
      {showLegend ? (
        <div className="mb-3 flex gap-4 text-xs text-content-muted">
          <LegendKey slot={1} label={t('stats.people.completed')} />
          <LegendKey slot={2} label={t('stats.people.open')} />
        </div>
      ) : null}
      <ul className="space-y-2">
        {rows.map((row) => {
          const total = row.open + row.completed;
          return (
            <li
              key={row.user_id}
              className="grid grid-cols-[minmax(0,9rem)_1fr_auto] items-center gap-3 text-sm"
              onPointerEnter={() => setHover(row.user_id)}
              onPointerLeave={() => setHover(null)}
            >
              <span className="truncate text-content">{row.name}</span>
              <div className="relative h-6" title={`${row.completed} / ${row.open}`}>
                <div className="flex h-full items-center gap-[2px]">
                  <Bar slot={1} width={(row.completed / max) * 100} rounded={row.open === 0} />
                  <Bar slot={2} width={(row.open / max) * 100} rounded />
                </div>
                {hover === row.user_id ? (
                  <Tooltip
                    left={Math.min(90, (total / max) * 100)}
                    rows={[
                      { slot: 1, label: t('stats.people.completed'), value: String(row.completed) },
                      { slot: 2, label: t('stats.people.open'), value: String(row.open) },
                      ...(row.median_days_to_complete !== null
                        ? [{ label: t('stats.person.median', { value: row.median_days_to_complete }), value: '' }]
                        : []),
                      ...(row.p90_days_to_complete !== null
                        ? [{ label: t('stats.person.p90', { value: row.p90_days_to_complete }), value: '' }]
                        : []),
                    ]}
                  />
                ) : null}
              </div>
              <span className="w-8 text-right text-xs tabular-nums text-content-muted">{total}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hours — one series
// ---------------------------------------------------------------------------

function HoursBars({ rows }: { rows: Stats['by_user'] }) {
  const sorted = [...rows].sort((a, b) => b.tracked_minutes - a.tracked_minutes);
  const max = Math.max(1, ...sorted.map((r) => r.tracked_minutes));
  return (
    <ul className="space-y-2">
      {sorted.map((row) => (
        <li key={row.user_id} className="grid grid-cols-[minmax(0,9rem)_1fr_auto] items-center gap-3 text-sm">
          <span className="truncate text-content">{row.name}</span>
          <div className="flex h-6 items-center" title={formatMinutes(row.tracked_minutes)}>
            <Bar slot={1} width={(row.tracked_minutes / max) * 100} rounded />
          </div>
          <span className="w-14 text-right text-xs tabular-nums text-content-muted">
            {hours(row.tracked_minutes)}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Projects — a table: more than a handful of classes is a table's job
// ---------------------------------------------------------------------------

function ProjectsTable({ rows }: { rows: Stats['by_project'] }) {
  const t = useT();
  const sorted = [...rows].sort((a, b) => b.tracked_minutes - a.tracked_minutes);
  const max = Math.max(1, ...sorted.map((r) => r.tracked_minutes));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wide text-content-subtle">
            <th className="pb-2 font-medium">{t('stats.projects.project')}</th>
            <th className="pb-2 text-right font-medium">{t('stats.projects.open')}</th>
            <th className="pb-2 text-right font-medium">{t('stats.projects.completed')}</th>
            <th className="pb-2 pl-6 font-medium">{t('stats.projects.hours')}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={row.project_id ?? 'none'} className="border-t border-border">
              <td className="py-2">
                <span className="flex items-center gap-2">
                  {row.color ? (
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: `var(--color-label-${row.color})` }}
                      aria-hidden
                    />
                  ) : null}
                  <span className={cn(!row.name && 'text-content-subtle')}>
                    {row.name ?? t('stats.projects.none')}
                  </span>
                </span>
              </td>
              <td className="py-2 text-right tabular-nums text-content-muted">{row.open}</td>
              <td className="py-2 text-right tabular-nums text-content-muted">{row.completed}</td>
              <td className="py-2 pl-6">
                <span className="flex items-center gap-3">
                  <span className="flex h-4 w-32 items-center">
                    <Bar slot={1} width={(row.tracked_minutes / max) * 100} rounded />
                  </span>
                  <span className="tabular-nums text-content-muted">{hours(row.tracked_minutes)}</span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Matrix — who worked on what: a heat table, one hue light→dark
// ---------------------------------------------------------------------------

function Matrix({ data }: { data: Stats }) {
  const t = useT();
  const people = data.by_user.filter((u) => u.tracked_minutes > 0);
  const projects = data.by_project.filter((p) => p.tracked_minutes > 0);
  const cell = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of data.by_user_project) {
      map.set(`${row.user_id}|${row.project_id ?? 'none'}`, row.tracked_minutes);
    }
    return map;
  }, [data.by_user_project]);
  const max = Math.max(1, ...data.by_user_project.map((r) => r.tracked_minutes));

  if (people.length === 0 || projects.length === 0) return null;

  return (
    <div className="overflow-x-auto">
      <table className="text-sm">
        <thead>
          <tr>
            <th />
            {projects.map((p) => (
              <th
                key={p.project_id ?? 'none'}
                className="max-w-28 truncate px-2 pb-2 text-left text-[11px] font-medium uppercase tracking-wide text-content-subtle"
              >
                {p.name ?? t('stats.projects.none')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {people.map((u) => (
            <tr key={u.user_id}>
              <td className="max-w-36 truncate pr-3 text-content">{u.name}</td>
              {projects.map((p) => {
                const minutes = cell.get(`${u.user_id}|${p.project_id ?? 'none'}`) ?? 0;
                const level = minutes === 0 ? 0 : Math.max(1, Math.round((minutes / max) * 5));
                return (
                  <td key={p.project_id ?? 'none'} className="p-[2px]">
                    <div
                      className={cn('viz-cell flex h-9 min-w-16 items-center justify-center rounded text-xs tabular-nums', `viz-cell-${level}`)}
                      title={`${u.name} · ${p.name ?? t('stats.projects.none')} · ${formatMinutes(minutes)}`}
                    >
                      {minutes > 0 ? hours(minutes) : ''}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Handover tile
// ---------------------------------------------------------------------------

function HandoverTile({ data }: { data: Stats['handovers'] }) {
  const t = useT();
  if (data.sent === 0 && data.returned === 0 && data.open === 0) return null;
  return (
    <Card title={t('stats.handover.title')}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile label={t('stats.handover.sent')} value={String(data.sent)} />
        <Tile label={t('stats.handover.returned')} value={String(data.returned)} />
        <Tile label={t('stats.handover.open')} value={String(data.open)} tone={data.open > 0 ? 'warning' : 'plain'} />
        <Tile
          label={t('stats.handover.turnaround')}
          value={data.median_hours_to_return === null ? '—' : t('stats.hours', { value: data.median_hours_to_return })}
        />
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-surface p-4">
      <h2 className="mb-3 text-sm font-medium text-content">{title}</h2>
      {children}
    </section>
  );
}

function Segmented({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="flex flex-wrap items-center gap-1">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          aria-pressed={value === option.value}
          className={cn(
            'rounded-full px-2.5 py-1 text-xs transition-surface',
            value === option.value
              ? 'bg-accent/10 font-medium text-accent'
              : 'text-content-muted hover:bg-surface-hover hover:text-content',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** <= 24px thick, rounded at the data end, square at the baseline. */
function Bar({ slot, width, rounded }: { slot: 1 | 2; width: number; rounded: boolean }) {
  if (width <= 0) return null;
  return (
    <span
      className={cn('block h-4', `viz-fill-${slot}`, rounded && 'rounded-r')}
      style={{ width: `${Math.max(width, 0.5)}%` }}
    />
  );
}

function LegendKey({ slot, label }: { slot: 1 | 2; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={cn('h-2.5 w-2.5 rounded-sm', `viz-fill-${slot}`)} aria-hidden />
      {label}
    </span>
  );
}

function Tooltip({
  left,
  rows,
}: {
  left: number;
  rows: { slot?: 1 | 2; label: string; value: string }[];
}) {
  return (
    <div
      className="pointer-events-none absolute -top-2 z-10 -translate-x-1/2 -translate-y-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs shadow-lg"
      style={{ left: `${left}%` }}
    >
      {rows.map((row, i) => (
        <div key={i} className="flex items-center gap-2 whitespace-nowrap">
          {row.slot ? <span className={cn('h-0.5 w-3', `viz-fill-${row.slot}`)} aria-hidden /> : null}
          {/* Values lead, labels follow. */}
          {row.value ? <span className="font-semibold text-content">{row.value}</span> : null}
          <span className="text-content-muted">{row.label}</span>
        </div>
      ))}
    </div>
  );
}

function niceTicks(max: number): number[] {
  const step = max <= 5 ? 1 : max <= 10 ? 2 : max <= 25 ? 5 : Math.ceil(max / 5 / 10) * 10;
  const ticks: number[] = [];
  for (let v = 0; v <= max; v += step) ticks.push(v);
  return ticks;
}

function hours(minutes: number): string {
  return `${(minutes / 60).toFixed(minutes % 60 === 0 ? 0 : 1)} h`;
}

function shortDay(iso: string): string {
  const [, month, day] = iso.split('-').map(Number);
  return `${day}.${month}.`;
}

/**
 * The palette. Slot 1 is the brand's own green hue at a chroma that clears
 * the validator's floor; slot 2 is the reference blue. Both modes were run
 * through the dataviz validator against MirOnDo's actual surfaces (#ffffff
 * and the dark canvas) — all checks pass, worst adjacent CVD ΔE 21.3 light /
 * 16.0 dark. Text never wears a series colour: identity comes from the mark
 * beside it.
 */
const VIZ_TOKENS = `
.viz-root {
  --viz-1: #007f35;
  --viz-2: #2a78d6;
  --viz-grid: var(--color-border);
  --viz-ink: var(--color-content-subtle);
}
.dark .viz-root {
  --viz-1: #47a34e;
  --viz-2: #3987e5;
}
.viz-fill-1 { background: var(--viz-1); }
.viz-fill-2 { background: var(--viz-2); }
.viz-line { stroke: var(--viz-1); stroke-width: 2; stroke-linejoin: round; stroke-linecap: round; }
.viz-area { fill: var(--viz-1); fill-opacity: 0.1; }
.viz-dot { fill: var(--viz-1); stroke: var(--color-surface); stroke-width: 2; }
.viz-grid { stroke: var(--viz-grid); stroke-width: 1; }
.viz-crosshair { stroke: var(--viz-ink); stroke-width: 1; }
.viz-tick { fill: var(--viz-ink); font-size: 10px; }
.viz-value { fill: var(--color-content); font-size: 11px; font-weight: 600; }
.viz-cell-0 { background: var(--color-surface-sunken); color: var(--color-content-subtle); }
.viz-cell-1 { background: color-mix(in oklch, var(--viz-1) 18%, var(--color-surface)); color: var(--color-content); }
.viz-cell-2 { background: color-mix(in oklch, var(--viz-1) 34%, var(--color-surface)); color: var(--color-content); }
.viz-cell-3 { background: color-mix(in oklch, var(--viz-1) 52%, var(--color-surface)); color: white; }
.viz-cell-4 { background: color-mix(in oklch, var(--viz-1) 72%, var(--color-surface)); color: white; }
.viz-cell-5 { background: var(--viz-1); color: white; }
`;
